import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Row, 
  Col, 
  Typography, 
  Avatar, 
  Alert,
  Divider
} from 'antd';
import { 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  BuildOutlined,
  DollarOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { getSpecializationLabel } from '../../constants/specializations';
import ProviderSelectionModal from '../ProviderSelectionModal';
import type { GetAppointmentQuery } from '../../generated/graphql';

const { Title, Text } = Typography;

type Provider = NonNullable<GetAppointmentQuery['appointment']>['provider'];
type ProviderDoctor = NonNullable<Provider>['doctor'];
type ProviderClinic = NonNullable<Provider>['clinic'];

interface ProviderInformationCardProps {
  provider?: Provider;
  appointmentId: string;
  onProviderUpdated?: () => void;
  style?: React.CSSProperties;
  showAssignButton?: boolean;
  showChangeButton?: boolean;
}

const ProviderInformationCard: React.FC<ProviderInformationCardProps> = ({
  provider,
  appointmentId,
  onProviderUpdated,
  style,
  showAssignButton = true,
  showChangeButton = true
}) => {
  const { t, currentLanguage } = useTranslation();
  const [isProviderModalVisible, setIsProviderModalVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Helper function to get doctor display name based on current language
  const getDoctorDisplayName = () => {
    if (!provider?.doctor) return '';
    
    // Try to detect language preference from the current locale
    const currentLang = t('language.en') === 'English' ? 'en' : 'zh';
    
    if (currentLang === 'zh') {
      // Show Chinese name first, then English in parentheses
      const chineseName = `${provider.doctor.last_name_zh_hk || ''} ${provider.doctor.first_name_zh_hk || ''}`.trim();
      const englishName = `${provider.doctor.first_name_en || ''} ${provider.doctor.last_name_en || ''}`.trim();
      
      if (chineseName && englishName) {
        return `${chineseName} (${englishName})`;
      } else if (chineseName) {
        return chineseName;
      } else if (englishName) {
        return englishName;
      }
    } else {
      // Show English name first, then Chinese in parentheses
      const englishName = `${provider.doctor.first_name_en || ''} ${provider.doctor.last_name_en || ''}`.trim();
      const chineseName = `${provider.doctor.last_name_zh_hk || ''} ${provider.doctor.first_name_zh_hk || ''}`.trim();
      
      if (englishName && chineseName) {
        return `${englishName} (${chineseName})`;
      } else if (englishName) {
        return englishName;
      } else if (chineseName) {
        return chineseName;
      }
    }
    
    return provider.doctor.first_name_en || provider.doctor.first_name_zh_hk || 'Unknown';
  };

  // Helper function to get clinic display name based on current language
  const getClinicDisplayName = () => {
    if (!provider?.clinic) return '';
    
    // Use internal_name as the primary display name
    return provider.clinic.internal_name || provider.clinic.name_zh_hk || provider.clinic.name_en || 'Unknown';
  };

  const handleProviderAssigned = () => {
    setIsProviderModalVisible(false);
    onProviderUpdated?.();
  };

  const shouldShowActionButton = (provider && showChangeButton) || (!provider && showAssignButton);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      <Card 
        title={
          <div 
            style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              width: '100%'
            }}
            onClick={toggleCollapse}
          >
            <span>{t('appointments.providerInformation')}</span>
            <Space>
              {shouldShowActionButton && (
                <Button 
                  type="dashed" 
                  icon={<UserOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsProviderModalVisible(true);
                  }}
                  size="small"
                >
                  {provider ? t('appointments.changeProvider') : t('appointments.assignProvider')}
                </Button>
              )}
                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {isCollapsed ? <DownOutlined /> : <UpOutlined />}
              </span>
            </Space>
          </div>
        }
        style={{ marginBottom: 24, ...style }}
      >
        {!isCollapsed && (
          <>
            {provider ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card size="small" title={t('appointments.doctor')} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <Avatar 
                        size={48}
                        src={provider.doctor.avatar_url || undefined}
                        style={{ backgroundColor: '#1890ff' }}
                        icon={<UserOutlined />}
                      />
                      <div style={{ marginLeft: 12 }}>
                        <Title level={5} style={{ margin: 0 }}>
                          {getDoctorDisplayName()}
                        </Title>
                        {provider.doctor.specialization && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {provider.doctor.specialization}
                          </Text>
                        )}
                      </div>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <Space direction="vertical" size={2}>
                      <div style={{ fontSize: '12px' }}>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        <a href={`tel:${provider.doctor.phone_number}`}>
                          {provider.doctor.phone_number}
                        </a>
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <MailOutlined style={{ marginRight: 4 }} />
                        {provider.doctor.email}
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title={t('appointments.clinic')} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <BuildOutlined style={{ marginRight: 8, color: '#8c8c8c', fontSize: '24px' }} />
                      <div>
                        <Title level={5} style={{ margin: 0 }}>
                          {getClinicDisplayName()}
                        </Title>
                      </div>
                    </div>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <div style={{ fontSize: '12px' }}>
                        <BuildOutlined style={{ marginRight: 4 }} />
                        {provider.clinic.address}
                        {provider.clinic.building_name && `, ${provider.clinic.building_name}`}
                        {provider.clinic.floor && `, ${provider.clinic.floor}`}
                        {provider.clinic.unit && `, ${provider.clinic.unit}`}
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        {provider.clinic.district}, {provider.clinic.region}
                      </div>
                    </Space>
                    <Divider style={{ margin: '12px 0' }} />
                    <Space direction="vertical" size={2}>
                      <div style={{ fontSize: '12px' }}>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {provider.clinic.phone_number}
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <MailOutlined style={{ marginRight: 4 }} />
                        {provider.clinic.email}
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>
            ) : (
              <Alert
                message={t('appointments.noProviderAssigned')}
                description={t('appointments.noProviderAssignedDescription')}
                type="warning"
                showIcon
                action={
                  showAssignButton ? (
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={() => setIsProviderModalVisible(true)}
                    >
                      {t('appointments.assignProvider')}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </>
        )}
        {isCollapsed && provider && (
          <div style={{ padding: '8px 0' }}>
            <Space>
              <Avatar 
                size={32}
                src={provider.doctor.avatar_url || undefined}
                style={{ backgroundColor: '#1890ff' }}
                icon={<UserOutlined />}
              />
              <div>
                <Text strong>
                  {getDoctorDisplayName()}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {provider.doctor.specialization && `${getSpecializationLabel(provider.doctor.specialization, currentLanguage)} • `}{getClinicDisplayName()}
                </Text>
              </div>
            </Space>
          </div>
        )}
      </Card>

      {/* Provider Selection Modal */}
      <ProviderSelectionModal
        visible={isProviderModalVisible}
        appointmentId={appointmentId}
        onCancel={() => setIsProviderModalVisible(false)}
        onAssigned={handleProviderAssigned}
      />
    </>
  );
};

export default ProviderInformationCard;
